import {
    addMonths,
    addQuarters,
    addYears,
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    isValid,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears
} from 'date-fns';
import type { SetStateAction } from 'react';
import React, { useEffect, useMemo } from 'react';

// Types
export type PeriodType = '24H' | '1W' | '1M' | '1Q' | '1Y' | 'CUSTOM'
export type HistoricalPeriod = string | "THIS_MONTH" | "THIS_QUARTER" | "THIS_YEAR" // e.g., "THIS_MONTH", "M1-2024", "THIS_QUARTER", "Q1-2025", "THIS_YEAR", "Y2024"

export const isDynamicPeriod = (period: HistoricalPeriod | null): boolean => ["THIS_MONTH", "THIS_QUARTER", "THIS_YEAR"].includes(period as string)

export interface TimeRange {
    dateFrom: Date
    dateTo: Date
}

export interface UseTimeRangeReturn {
    periodType: PeriodType
    setPeriodType: (type: PeriodType) => void
    historicalPeriod: HistoricalPeriod | null
    setHistoricalPeriod: (period: HistoricalPeriod | null) => void
    customRange: TimeRange | null
    setCustomRange: (value: SetStateAction<TimeRange | null>) => void
    timeRange: TimeRange
}

// Constants
export const HISTORICAL_COUNTS = {
    months: 24, // Last 24 months
    quarters: 8, // Last 8 quarters
    years: 3, // Last 3 years
}

export const PERIOD_TYPES = [
    { value: '24H' as PeriodType, label: 'Posledních 24 hodin' },
    { value: '1W' as PeriodType, label: 'Poslední týden' },
    { value: '1M' as PeriodType, label: 'Měsíc' },
    { value: '1Q' as PeriodType, label: 'Čtvrtletí' },
    { value: '1Y' as PeriodType, label: 'Rok' },
    { value: 'CUSTOM' as PeriodType, label: 'Vlastní rozsah' },
]

// Helper function to compute time range
export const getTimeRange = (
    periodType: PeriodType | null,
    historicalPeriod: HistoricalPeriod | null,
    customRange: TimeRange | null
): TimeRange => {
    const now = new Date()
    let dateFrom: Date
    let dateTo: Date

    if (periodType === 'CUSTOM' && customRange && isValid(customRange.dateFrom) && isValid(customRange.dateTo)) {
        return customRange
    }

    if (periodType === '1M' && historicalPeriod) {
        if (historicalPeriod === 'THIS_MONTH') {
            dateFrom = startOfMonth(now)
            dateTo = endOfDay(now)
        } else {
            const match = historicalPeriod.match(/M(\d{1,2})-(\d{4})/)
            if (match) {
                const [, month, year] = match
                dateFrom = new Date(parseInt(year), parseInt(month) - 1, 1)
                dateTo = endOfMonth(dateFrom)
            } else {
                console.warn(`Invalid month format: ${historicalPeriod}`)
                dateFrom = subDays(now, 1)
                dateTo = endOfDay(now)
            }
        }
    } else if (periodType === '1Q' && historicalPeriod) {
        if (historicalPeriod === 'THIS_QUARTER') {
            dateFrom = startOfQuarter(now)
            dateTo = endOfDay(now)
        } else {
            const match = historicalPeriod.match(/Q(\d)-(\d{4})/)
            if (match) {
                const [, quarter, year] = match
                const quarterStartMonth = (parseInt(quarter) - 1) * 3
                dateFrom = new Date(parseInt(year), quarterStartMonth, 1)
                dateTo = endOfQuarter(dateFrom)
            } else {
                console.warn(`Invalid quarter format: ${historicalPeriod}`)
                dateFrom = subDays(now, 1)
                dateTo = endOfDay(now)
            }
        }
    } else if (periodType === '1Y' && historicalPeriod) {
        if (historicalPeriod === 'THIS_YEAR') {
            dateFrom = startOfYear(now)
            dateTo = endOfDay(now)
        } else {
            const match = historicalPeriod.match(/Y(\d{4})/)
            if (match) {
                const [, year] = match
                dateFrom = new Date(parseInt(year), 0, 1)
                dateTo = endOfYear(dateFrom)
            } else {
                console.warn(`Invalid year format: ${historicalPeriod}`)
                dateFrom = subDays(now, 1)
                dateTo = endOfDay(now)
            }
        }
    } else {
        switch (periodType) {
            case '24H':
                dateFrom = subDays(now, 1)
                dateTo = endOfDay(now)
                break
            case '1W':
                dateFrom = subWeeks(now, 1)
                dateTo = endOfDay(now)
                break
            case '1M':
                // Default to previous month for consistency
                dateFrom = startOfMonth(subMonths(now, 1))
                dateTo = endOfMonth(dateFrom)
                break
            case '1Q':
                // Default to previous quarter
                dateFrom = startOfQuarter(subQuarters(now, 1))
                dateTo = endOfQuarter(dateFrom)
                break
            case '1Y':
                // Default to previous year
                dateFrom = startOfYear(subYears(now, 1))
                dateTo = endOfYear(dateFrom)
                break
            default:
                console.warn(`Unknown period type: ${periodType}`)
                dateFrom = subDays(now, 1)
                dateTo = endOfDay(now)
        }
    }

    return { dateFrom, dateTo }
}

// Helper function to calculate previous or next time range
export const getOffsetHistoricalPeriod = (
    historicalPeriod: HistoricalPeriod | null,
    offset: number
): [PeriodType, HistoricalPeriod] | null => {
    if (!historicalPeriod)
        return null

    const now = new Date()

    if (historicalPeriod === 'THIS_MONTH') {
        const offsetDate = addMonths(startOfMonth(now), offset)
        return ['1M', `M${offsetDate.getMonth() + 1}-${offsetDate.getFullYear()}`]
    } else if (historicalPeriod === 'THIS_QUARTER') {
        const offsetDate = addQuarters(startOfQuarter(now), offset)
        const offsetQuarter = Math.floor(offsetDate.getMonth() / 3) + 1
        return ['1Q', `Q${offsetQuarter}-${offsetDate.getFullYear()}`]
    } else if (historicalPeriod === 'THIS_YEAR') {
        const offsetDate = addYears(startOfYear(now), offset)
        return ['1Y', `Y${offsetDate.getFullYear()}`]
    } else if (historicalPeriod.startsWith('M')) {
        const match = historicalPeriod.match(/M(\d{1,2})-(\d{4})/)
        if (match) {
            const [, month, year] = match
            const offsetDate = addMonths(new Date(parseInt(year), parseInt(month) - 1, 1), offset)
            return ['1M', `M${offsetDate.getMonth() + 1}-${offsetDate.getFullYear()}`]
        }
    } else if (historicalPeriod.startsWith('Q')) {
        const match = historicalPeriod.match(/Q(\d)-(\d{4})/)
        if (match) {
            const [, quarter, year] = match
            const quarterStartMonth = (parseInt(quarter) - 1) * 3
            const offsetDate = addQuarters(new Date(parseInt(year), quarterStartMonth, 1), offset)
            const offsetQuarter = Math.floor(offsetDate.getMonth() / 3) + 1
            return ['1Q', `Q${offsetQuarter}-${offsetDate.getFullYear()}`]
        }
    } else if (historicalPeriod.startsWith('Y')) {
        const match = historicalPeriod.match(/Y(\d{4})/)
        if (match) {
            const [, year] = match
            const offsetDate = addYears(new Date(parseInt(year), 0, 1), offset)
            return ['1Y', `Y${offsetDate.getFullYear()}`]
        }
    }

    console.warn(`Invalid historical period format: ${historicalPeriod}`)
    return null
}

// Helper function to generate historical periods
export const generateHistoricalPeriods = (periodType: PeriodType, count: number) => {
    const now = new Date()
    const periods: { value: HistoricalPeriod; label: string }[] = []

    if (periodType === '1M') {
        periods.push({ value: 'THIS_MONTH', label: getHistoricalLabel(periodType, 'THIS_MONTH') })
        for (let i = 1; i <= count; i++) {
            const date = subMonths(now, i)
            const month = date.getMonth()
            const year = date.getFullYear()
            const value = `M${month + 1}-${year}`
            periods.push({
                value,
                label: getHistoricalLabel(periodType, value),
            })
        }
    } else if (periodType === '1Q') {
        periods.push({ value: 'THIS_QUARTER', label: getHistoricalLabel(periodType, 'THIS_QUARTER') })
        for (let i = 1; i <= count; i++) {
            const date = subQuarters(now, i)
            const quarter = Math.floor(date.getMonth() / 3) + 1
            const year = date.getFullYear()
            const value = `Q${quarter}-${year}`
            periods.push({
                value,
                label: getHistoricalLabel(periodType, value),
            })
        }
    } else if (periodType === '1Y') {
        periods.push({ value: 'THIS_YEAR', label: getHistoricalLabel(periodType, 'THIS_YEAR') })
        for (let i = 1; i <= count; i++) {
            const date = subYears(now, i)
            const year = date.getFullYear()
            const value = `Y${year}`
            periods.push({
                value,
                label: getHistoricalLabel(periodType, value),
            })
        }
    }

    return periods
}

// Get display label for historical period dropdown
export const getHistoricalLabel = (periodType: PeriodType | null, historicalPeriod: HistoricalPeriod | null, includeYear: boolean = true) => {
    if (!periodType) {
        return 'Neznámý rozsah'
    }
    if (periodType === 'CUSTOM') {
        return 'Vlastní rozsah'
    }
    if (['24H', '1W'].includes(periodType)) {
        return PERIOD_TYPES.find((opt) => opt.value === periodType)?.label || 'Select Period'
    }

    if (periodType === '1M' && historicalPeriod) {
        const match = historicalPeriod.match(/M(\d{1,2})-(\d{4})/)
        if (match) {
            const [, month, year] = match
            return format(new Date(parseInt(year), parseInt(month) - 1), includeYear ? 'LLLL yyyy' : 'LLLL')
        }
        if (historicalPeriod === 'THIS_MONTH') {
            return format(new Date(), includeYear ? 'LLLL yyyy' : 'LLLL')
        }
    }
    if (periodType === '1Q' && historicalPeriod) {
        const match = historicalPeriod.match(/Q(\d)-(\d{4})/)
        if (match) {
            const [, quarter, year] = match
            const quarterDate = new Date(parseInt(year), (parseInt(quarter) - 1) * 3, 1)
            return "Q" + format(quarterDate, includeYear ? 'Q yyyy' : 'Q')
        }
        if (historicalPeriod === 'THIS_QUARTER') {
            return "Q" + format(new Date(), includeYear ? 'Q yyyy' : 'Q')
        }
    }
    if (periodType === '1Y' && historicalPeriod) {
        const match = historicalPeriod.match(/Y(\d{4})/)
        if (match) {
            const [, year] = match
            return year
        }
        if (historicalPeriod === 'THIS_YEAR') {
            return new Date().getFullYear().toString()
        }
    }
    return 'Select Exact Period'
}

// Custom hook
export const useTimeRange = (defaultPeriodType: PeriodType = '1M', defaultHistoricalPeriod: HistoricalPeriod = 'THIS_MONTH'): UseTimeRangeReturn => {
    const [periodType, _setPeriodType] = React.useState<PeriodType>(defaultPeriodType)
    const [historicalPeriod, setHistoricalPeriod] = React.useState<HistoricalPeriod | null>(defaultHistoricalPeriod)
    const [customRange, setCustomRange] = React.useState<TimeRange | null>(null)
    const timeRange = useMemo(
        () => getTimeRange(periodType, historicalPeriod, customRange),
        [periodType, historicalPeriod, customRange]
    )

    const setPeriodType = (newPeriodType: PeriodType) => {
        if (newPeriodType === 'CUSTOM') {
            setCustomRange(timeRange)
        }
        _setPeriodType(newPeriodType)
    }

    // Set historical period to latest value when period type changes
    useEffect(() => {
        if (periodType === '1M') {
            setHistoricalPeriod('THIS_MONTH')
        } else if (periodType === '1Q') {
            setHistoricalPeriod('THIS_QUARTER')
        } else if (periodType === '1Y') {
            setHistoricalPeriod('THIS_YEAR')
        } else {
            setHistoricalPeriod(null)
        }
    }, [periodType])

    return {
        periodType,
        setPeriodType,
        historicalPeriod,
        setHistoricalPeriod,
        customRange,
        setCustomRange,
        timeRange,
    }
}